import { AlertCircle, Inbox, Search, SearchX } from "lucide-react";
import { useMemo, useState } from "react";

import { AppShell } from "components/AppShell";
import { SectionHeader } from "components/SectionHeader";
import { EmptyState, LoadingCards } from "components/states";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { SECTION_TITLES } from "../../copy/section-titles";

import { EventTile } from "../dashboard/EventTile";
import { RegisteredChip } from "../dashboard/EventTileParts";
import { useMyMemberships } from "../dashboard/use-my-memberships";
import { useToday } from "../dashboard/use-today";
import type { PublicEvent } from "./schemas";
import { usePublishedEvents } from "./use-published-events";

/**
 * Every published event, for anyone signed in (D12).
 *
 * The dashboard answers "what am I in"; this answers "what is there". Where
 * the membership query has answered, this page marks the events the person is
 * already in. That badge is decoration and it is best effort: the memberships
 * resolve on their own schedule, and a card carries no badge while that query
 * is still running or if it fails.
 *
 * Every card links to /e/{slug}, which is where registering happens. Putting a
 * Register button on a card here would be a second write path into
 * registration_requests, and it would fire without the person having seen the
 * event's dates, description or the secret prompt that some events answer
 * with.
 */
export function BrowseEventsPage({
  uid,
  email,
  signOut,
}: {
  uid: string;
  email: string | null;
  signOut: () => void;
}) {
  const state = usePublishedEvents();
  const memberships = useMyMemberships(uid);
  const [search, setSearch] = useState("");
  // The dates are written against it; re-read when the app returns to the front.
  const today = useToday();

  // Only the success case badges anything. A membership query still loading,
  // or refused, leaves every card unbadged, which understates what the reader
  // is in and never overstates it.
  const joinedEventIds = useMemo(
    () =>
      memberships.status === "success"
        ? new Set(memberships.memberships.map((m) => m.eventId))
        : new Set<string>(),
    [memberships],
  );

  const loaded = state.status === "success" ? state.events : [];
  const matches = useMemo(() => filter(loaded, search), [loaded, search]);
  const filtering = search.trim().length > 0;

  return (
    <AppShell identity={{ email }} onSignOut={signOut} title="All Events">
      <div className="mx-auto w-full max-w-6xl">
        <SectionHeader
          kicker={SECTION_TITLES.browseEvents.kicker}
          title={SECTION_TITLES.browseEvents.title}
          className="mb-6"
        />

        {state.status === "loading" && <LoadingCards cards={3} mode="grid" />}

        {state.status === "error" && (
          <EmptyState
            icon={AlertCircle}
            title="Could not load events"
            testId="browse-error"
          >
            {state.error}
          </EmptyState>
        )}

        {state.status === "success" && (
          <>
            {state.events.length > 0 && (
              <div className="mb-5 max-w-sm">
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <Input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search by title or location"
                    aria-label="Search events"
                    testId="browse-search"
                    className="pl-11"
                  />
                </div>
                {filtering && state.hasMore && (
                  <p
                    className="mt-2 text-sm text-muted-foreground"
                    data-testid="browse-search-scope"
                  >
                    Searching the {state.events.length} events loaded so far.
                    Show more to search the rest.
                  </p>
                )}
              </div>
            )}

            {/* Not while another page is waiting: a page of archived events
                hides every card, and that is not "no events". */}
            {state.events.length === 0 && !state.hasMore && (
              <EmptyState
                icon={Inbox}
                title="No published events"
                testId="browse-empty"
              >
                An event appears here once its organisers publish it.
              </EmptyState>
            )}

            {state.events.length > 0 && matches.length === 0 && (
              <EmptyState
                icon={SearchX}
                title="No events match that search"
                testId="browse-no-matches"
              >
                {state.hasMore
                  ? "Try a shorter search, clear it, or show more events to search further back."
                  : "Try a shorter search, or clear it to see every event."}
              </EmptyState>
            )}

            {matches.length > 0 && (
              <div
                // Two columns from 360px, where a tile is still about 160px
                // wide; below that one column, so a title is not set in a
                // 100px strip. A fixed gap, not a fixed width: the tiles
                // share whatever the viewport leaves.
                className="grid grid-cols-1 gap-3 min-[360px]:grid-cols-2 sm:gap-4 lg:grid-cols-3"
                data-testid="browse-cards"
              >
                {matches.map((event) => (
                  <EventTile
                    key={event.eventId}
                    testId="browse-event-card"
                    today={today}
                    event={{
                      title: event.title,
                      startDate: event.startDate,
                      endDate: event.endDate,
                      location: event.location,
                      image: event.image,
                    }}
                    // The document id IS the address of a page that exists:
                    // the projection writes public_events/{slug} only for a
                    // published event with a slug.
                    target={
                      event.slug ? { page: "public", slug: event.slug } : null
                    }
                    badge={
                      joinedEventIds.has(event.eventId) ? (
                        <RegisteredChip />
                      ) : undefined
                    }
                  />
                ))}
              </div>
            )}

            {state.hasMore && (
              <div className="mt-6 flex flex-col items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => void state.loadMore()}
                  disabled={state.loadingMore}
                  data-testid="browse-load-more"
                >
                  {state.loadingMore ? "Loading..." : "Show more events"}
                </Button>
                {state.loadMoreError && (
                  <p
                    className="text-sm text-muted-foreground"
                    data-testid="browse-load-more-error"
                  >
                    Could not load more events. {state.loadMoreError}
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function filter(events: PublicEvent[], search: string): PublicEvent[] {
  const needle = search.trim().toLowerCase();
  if (needle === "") return events;
  return events.filter(
    (event) =>
      event.title.toLowerCase().includes(needle) ||
      event.location.toLowerCase().includes(needle),
  );
}
